import React, { useContext, useState, useEffect } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../helpers/AuthContext";
import AppInstanceList from "./AppInstanceList";
import NoDataContainer from "../../components/NoDataContainer";
import MainDashboardLeftSidebar from "../../pages/essentials/MainDashboardLeftSidear";
import "./MainDashboardReveal.css";

import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";


function HomeAnnouncements({ announcements, announcementsLoading, announcementsError }) {
  return (
    <aside className="ec-home-announcements" aria-label="Announcements">
      <div className="ec-home-announcements-card">
        <div className="ec-home-announcements-header">
          <div className="ec-home-announcements-heading">
            <span className="ec-home-announcements-mark" aria-hidden="true" />
            <h3>Announcements</h3>
          </div>
          <a href="https://elitecore.ca/category/announcements/" target="_blank" rel="noreferrer">View all →</a>
        </div>

        <div className="ec-home-announcements-body" aria-live="polite">
          {announcementsError ? (
            <div className="ec-home-announcements-error" role="status">{announcementsError}</div>
          ) : announcementsLoading ? (
            <div className="ec-home-announcements-loading" role="status" aria-label="Loading announcements">
              <span className="ec-home-announcements-spinner" aria-hidden="true" />
            </div>
          ) : !announcements.length ? (
            <div className="ec-home-announcements-message" role="status">No announcements right now.</div>
          ) : (
            <div className="ec-home-announcements-posts">
              {announcements.map((item, index) => (
                <a key={`${item.link || item.title || "announcement"}-${index}`}
                  className="ec-home-announcements-post"
                  href={item.link || "https://elitecore.ca/category/announcements/"}
                  target="_blank" rel="noreferrer">
                  <span className="ec-home-announcements-thumb">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" loading="lazy" onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }} />
                    ) : (
                      <CampaignOutlinedIcon fontSize="small" />
                    )}
                  </span>
                  <span className="ec-home-announcements-copy">
                    <strong>{item.title || "Announcement"}</strong>
                    {item.pubDate ? <small>{new Date(item.pubDate).toLocaleDateString()}</small> : null}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
        <p className="ec-home-announcements-caption">Stay up to date before jumping in.</p>
      </div>
    </aside>
  );
}

function Home() {
  const [listOfPosts, setListOfPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nonLoginLoading, setNonLoginLoading] = useState(true);
  const [noDataLoading, setNoDataLoading] = useState(true);

  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [announcementsError, setAnnouncementsError] = useState("");

  const { authState } = useContext(AuthContext);
  let history = useHistory();

  const getInstanceLogoUrl = (logo) => {
    if (logo && typeof logo === "string" && logo.startsWith("uploads")) {
      return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${logo}`;
    }
    return logo || "/default-core-logo.png";
  };

  useEffect(() => {
    let didCancel = false;

    const fetchInstances = async () => {
      if (!localStorage.getItem("accessToken")) {
        history.push("/login");
        return;
      }

      setNonLoginLoading(false);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byuserId/${authState.id}`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );

        if (didCancel) return;

        if (response.data) {
          const processedInstances = response.data.map((instance) => {
            instance.customUploadedBrandLogo = getInstanceLogoUrl(instance.customUploadedBrandLogo);
            return instance;
          });
          setListOfPosts(processedInstances);
          setLoading(false);
        } else {
          setNoDataLoading(false);
        }
      } catch (error) {
        if (didCancel) return;
        console.error("Error fetching instances:", error);
        setNoDataLoading(false);
      }
    };

    const fetchAnnouncements = async () => {
      try {
        setAnnouncementsLoading(true);
        setAnnouncementsError("");

        const r = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/announcements`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );

        if (didCancel) return;

        const items = r.data?.items || [];
        setAnnouncements(items.slice(0, 3));
      } catch (e) {
        if (didCancel) return;
        console.error("Announcements load failed:", e);
        setAnnouncements([]);
        setAnnouncementsError("Couldn’t load announcements.");
      } finally {
        if (!didCancel) setAnnouncementsLoading(false);
      }
    };

    fetchInstances();
    fetchAnnouncements();

    return () => {
      didCancel = true;
    };
  }, [authState.id, history]);

  return (
    <div className="ec-app-instances ec-home-reveal">
      {nonLoginLoading === false && (
        <>
          <MainDashboardLeftSidebar />

          <div className="rightSide ec-home-main">
            <div
              style={{
                display: "flex",
                gap: 18,
                width: "100%",
                position: "relative",
                alignItems: "flex-start",
              }}
            >
              {/* MAIN COLUMN */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {loading === false && listOfPosts && listOfPosts.length ? (
                  <div className="options-container fade-in-effect-usability ec-home-content">
                    <div className="options-inner-container">
                      <h1 className="main-page-heading ec-home-heading">Home</h1>

                      <span className="main-page-description ec-home-description">
                        You have {listOfPosts.length} active core.
                      </span>

                      <AppInstanceList list={{ list: listOfPosts }} />
                    </div>
                  </div>
                ) : noDataLoading === false ? (
                  <NoDataContainer NoDataMessage="There are currently no active cores." includeButton={true} />
                ) : (
                  ""
                )}
              </div>

              <HomeAnnouncements
                announcements={announcements}
                announcementsLoading={announcementsLoading}
                announcementsError={announcementsError}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Home;
